import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import Tabs from '@/components/Tabs';
import type {
  AgentApiSharedJobPostsContent,
  AgentApiSharedJobPostsItem,
} from '@/types/serializers';
import { linkifyText } from '@/utils/linkify';
import { OverflowTooltip } from '@/components/ui/overflow-tooltip';
import {
  MdApartment,
  MdOpenInNew,
  MdWork,
  MdLocationOn,
  MdBadge,
  MdPerson,
  MdSchool,
} from 'react-icons/md';
import { ImDatabase } from 'react-icons/im';
import MoneyBagIcon from '@/assets/icons/money_bag.svg?react';
import SuggestCandidatesModal from '@/features/agent/provided_job_posts/components/SuggestCandidatesModal';
import useBulkSuggestSharedJobPostCandidates from '../api/useBulkSuggestSharedJobPostCandidates';
import useSharedJobPostPdf from '../hooks/useSharedJobPostPdf';
import {
  showSuccessToast,
  showErrorToast,
  ToastProvider,
} from '@/features/agent/job_change_processes/lib/toast';

interface Props {
  post: AgentApiSharedJobPostsItem | null;
  open: boolean;
  onClose: () => void;
  initialTab?: ContentTab;
}

type ContentTab = 'job' | 'agent' | 'provider';

// 本文中のリンク（<a>）に適用するクラス
const LINK_CLASS = 'text-[#005FFF] underline hover:text-[#005FFF] hover:underline';

function ContentBlocks({ items }: { items: AgentApiSharedJobPostsContent[] }) {
  if (!items.length) {
    return null;
  }
  return (
    <div className="flex flex-col gap-6">
      {items.map((item, index) => (
        <div key={index} className="flex flex-col gap-2">
          <h3 className="text-base font-bold text-[#262626]">{item.label}</h3>
          <div className="text-sm leading-[1.7] break-words whitespace-pre-wrap text-[#262626]">
            {linkifyText(item.content, LINK_CLASS)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SharedJobPostDetailModal({ post, open, onClose, initialTab }: Props) {
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ContentTab>('job');
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const suggestCandidates = useBulkSuggestSharedJobPostCandidates();
  const { downloadPdf } = useSharedJobPostPdf();

  const postId = post?.id;
  const hasProviderInfo = Boolean(post?.provider_info?.trim());

  useEffect(() => {
    if (open && postId != null) {
      setActiveTab(initialTab ?? 'job');
    }
  }, [open, postId]);

  useEffect(() => {
    if (activeTab === 'provider' && !hasProviderInfo) {
      setActiveTab('job');
    }
  }, [activeTab, hasProviderInfo]);

  // タブ切替時にスクロール位置を先頭にリセット
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeTab, postId]);

  if (!post) return null;

  // 年収の表示を生成
  const formatSalary = () => {
    if (post.salary_info) {
      return post.salary_info;
    }
    if (post.min_salary && post.max_salary) {
      return `${post.min_salary}〜${post.max_salary} 万円`;
    }
    if (post.min_salary) {
      return `${post.min_salary}万円以上`;
    }
    if (post.max_salary) {
      return `${post.max_salary}万円以下`;
    }
    return null;
  };

  const salaryDisplay = formatSalary();

  const jobItems = post.job_content_json ?? [];
  const agentItems = post.agent_content_json ?? [];

  /** 公開URLをクリップボードにコピーする */
  const handleCopyPublicUrl = async () => {
    if (!post?.public_url) return;
    try {
      await navigator.clipboard.writeText(post.public_url);
      showSuccessToast('公開URLをコピーしました');
    } catch {
      showErrorToast('URLのコピーに失敗しました');
    }
  };

  /** PDFをダウンロードする */
  const handleDownloadPdf = async () => {
    if (!post) return;
    setIsPdfDownloading(true);
    try {
      await downloadPdf(post.id);
    } catch {
      showErrorToast('PDFのダウンロードに失敗しました');
    } finally {
      setIsPdfDownloading(false);
    }
  };

  const handleSuggestClick = () => {
    setIsSuggestModalOpen(true);
  };

  const handleSuggestSubmit = async (candidateIds: number[]) => {
    await suggestCandidates.mutateAsync({
      sharedJobPostIds: [post.id],
      candidateProfileIds: candidateIds,
    });
  };

  const handleDialogClose = () => {
    // SuggestCandidatesModalが開いている場合は親のDialogを閉じない
    if (isSuggestModalOpen) {
      return;
    }
    onClose();
  };

  const tabItems = [
    {
      label: '求人情報',
      onClick: () => setActiveTab('job'),
      current: activeTab === 'job',
    },
    {
      label: 'エージェント向け情報',
      onClick: () => setActiveTab('agent'),
      current: activeTab === 'agent',
    },
    ...(hasProviderInfo
      ? [
          {
            label: '提供元情報',
            onClick: () => setActiveTab('provider'),
            current: activeTab === 'provider',
          },
        ]
      : []),
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleDialogClose();
      }}
    >
      {/* 先頭の「公開URLコピー」に自動フォーカスされフォーカスリングが出るため抑止する */}
      <DialogContent
        className="flex h-[80vh] w-full max-w-[1100px] flex-col rounded shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]!"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* ヘッダー */}
        <div className="flex shrink-0 flex-col gap-3 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <DialogTitle className="min-w-0 flex-1 text-base! leading-normal font-bold! text-[#262626]">
              {post.title}
            </DialogTitle>
            <div className="flex shrink-0 items-center gap-2">
              {/* 公開URLコピーボタン */}
              {post.public_url && (
                <Button color="secondary" size="sm" onClick={handleCopyPublicUrl}>
                  公開URLコピー
                </Button>
              )}
              {/* PDFダウンロードボタン */}
              <Button
                color="secondary"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={isPdfDownloading}
              >
                {isPdfDownloading ? 'PDF生成中...' : 'PDF出力'}
              </Button>
              {/* 取り込み元ページへのリンク（jobmiru で host 未解決の場合など url が null のときは非表示 / #3010） */}
              {post.url && (
                <Button color="tertiary" size="sm" asChild>
                  <a
                    href={post.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1"
                  >
                    {post.provider_display_name}
                    <MdOpenInNew className="size-icon-xxs" />
                  </a>
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-3">
            {/* 企業名 */}
            <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
              <MdApartment size={20} className="shrink-0" />
              <p className="truncate text-xs">{post.company_name}</p>
            </div>

            {/* ポジション */}
            {post.job_position_name && (
              <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                <MdWork size={20} className="shrink-0" />
                <OverflowTooltip text={post.job_position_name} className="max-w-[200px] text-xs" />
              </div>
            )}

            {/* 年収 */}
            {salaryDisplay && (
              <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                <ImDatabase size={16} className="shrink-0" />
                <p className="truncate text-xs">{salaryDisplay}</p>
              </div>
            )}

            {/* 勤務地（都道府県コードが取れた求人は「全国」「東京都, 大阪府」形式 / Issue #3394） */}
            {post.working_location_label && (
              <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                <MdLocationOn size={20} className="shrink-0" />
                <p className="truncate text-xs">{post.working_location_label}</p>
              </div>
            )}

            {/* 雇用形態 */}
            {post.employment_type_label && (
              <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                <MdBadge size={20} className="shrink-0" />
                <p className="truncate text-xs">{post.employment_type_label}</p>
              </div>
            )}

            {/* 応募可能年齢 */}
            {post.age_label && (
              <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                <MdPerson size={20} className="shrink-0" />
                <p className="truncate text-xs">{post.age_label}</p>
              </div>
            )}

            {/* 最終学歴 */}
            {post.min_education_level_label && (
              <div className="flex items-center gap-1.5 border-r border-solid border-gray-300 pr-4">
                <MdSchool size={20} className="shrink-0" />
                <p className="truncate text-xs">{post.min_education_level_label}</p>
              </div>
            )}

            {/* 理論年収 */}
            {post.fee_info && (
              <div className="flex items-center gap-1.5">
                <MoneyBagIcon className="size-5 shrink-0" />
                <p className="truncate text-xs">{post.fee_info}</p>
              </div>
            )}
          </div>
        </div>

        {/* タブ + 本文 */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <Tabs items={tabItems} className="shrink-0 shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)]" />
          <div ref={contentRef} className="min-h-0 flex-1 overflow-y-auto p-6">
            {activeTab === 'job' &&
              (jobItems.length > 0 ? (
                <ContentBlocks items={jobItems} />
              ) : (
                <p className="text-sm text-gray-500">求人情報がありません</p>
              ))}

            {activeTab === 'agent' &&
              (agentItems.length > 0 ? (
                <ContentBlocks items={agentItems} />
              ) : (
                <p className="text-sm text-gray-500">エージェント向け情報がありません</p>
              ))}

            {activeTab === 'provider' && hasProviderInfo && (
              <div className="text-sm leading-[1.7] break-words whitespace-pre-wrap text-[#262626]">
                {linkifyText(post.provider_info ?? '', LINK_CLASS)}
              </div>
            )}
          </div>
        </div>

        {/* フッター */}
        <div className="flex shrink-0 items-center justify-end gap-4 border-t border-solid border-border px-6 py-4">
          <Button color="secondary" size="md" onClick={onClose}>
            キャンセル
          </Button>
          <Button color="primary" size="md" onClick={handleSuggestClick}>
            求職者に提案
          </Button>
        </div>
        {isSuggestModalOpen && (
          <SuggestCandidatesModal
            isOpen={isSuggestModalOpen}
            onClose={() => setIsSuggestModalOpen(false)}
            jobPosts={[
              {
                id: 0,
                companyName: post.company_name,
                position: post.job_position_name || '',
                location: post.working_location_label || '',
              },
            ]}
            onSubmit={handleSuggestSubmit}
            onSuccess={() => {
              setIsSuggestModalOpen(false);
              onClose();
            }}
            isSharedJobPost={true}
          />
        )}
        {/* Toastコンテナ: モーダル内のクリップボードコピー・PDFダウンロード通知に使用 */}
        <ToastProvider />
      </DialogContent>
    </Dialog>
  );
}
